// @givmo/observability — X-Client-* identification headers.
//
// The web parity for iOS's `GivmoRequestHeaders` (givmo-ios Givmo/Core/Networking/
// GivmoRequestHeaders.swift), which stamps every outgoing request with:
//   X-Client-Platform: ios-native
//   X-Client-Version:  CFBundleShortVersionString
//   X-Client-Build:    CFBundleVersion
//
// On web there is no bundle Info.plist, so platform/version/build are injected by the host
// app (from its build env — e.g. Vite `import.meta.env`). This package owns the header
// NAMES + the well-known platform tokens so every web client stamps them identically; the
// api-client (03-9) attaches the produced map to its fetch middleware.
//
// IMPORTANT: these are observability-only signals and MUST NOT be used as authorization
// primitives — anyone can forge a header. (Same warning as the iOS CLAUDE.md "X-Client-*
// headers" section.) Server-side version gating must go through attestation / authenticated
// feature flags, never these headers.

/** Canonical header names — single source of truth shared with the backend contract. */
export const CLIENT_HEADER_NAMES = {
  platform: 'X-Client-Platform',
  version: 'X-Client-Version',
  build: 'X-Client-Build',
} as const;

/**
 * Well-known `X-Client-Platform` tokens. iOS sends `ios-native`; each web client picks the
 * token for its app so backend telemetry can split web surfaces apart. Kept open (the type
 * is `string`) so a new app can introduce its own token without a package bump, but the
 * known set is enumerated for discoverability + autocomplete.
 */
export const CLIENT_PLATFORMS = {
  donateWeb: 'donate-web',
  consumerWeb: 'consumer-web',
  adminWeb: 'admin-web',
  superAdminWeb: 'super-admin-web',
  payWeb: 'pay-web',
  charityWeb: 'charity-web',
  boardWeb: 'board-web',
  companyWeb: 'company-web',
} as const;

export type KnownClientPlatform = (typeof CLIENT_PLATFORMS)[keyof typeof CLIENT_PLATFORMS];

export interface ClientIdentity {
  /** `X-Client-Platform` token (e.g. `donate-web`). A `KnownClientPlatform` or any string. */
  platform: KnownClientPlatform | (string & {});
  /** `X-Client-Version` — the app's semantic/marketing version (parity: CFBundleShortVersionString). */
  version: string;
  /** `X-Client-Build` — the build identifier (parity: CFBundleVersion). Optional; omitted if unset. */
  build?: string;
}

/**
 * Build the `X-Client-*` header map for a request. Returns a plain `Record<string,string>`
 * the api-client merges into its fetch headers. Blank/whitespace-only values are omitted so
 * a missing build env never produces an empty `X-Client-Build: ` header. Build is omitted
 * entirely when absent.
 */
export function clientHeaders(identity: ClientIdentity): Record<string, string> {
  const headers: Record<string, string> = {};
  const platform = identity.platform.trim();
  const version = identity.version.trim();
  if (platform.length > 0) headers[CLIENT_HEADER_NAMES.platform] = platform;
  if (version.length > 0) headers[CLIENT_HEADER_NAMES.version] = version;
  const build = identity.build?.trim();
  if (build !== undefined && build.length > 0) {
    headers[CLIENT_HEADER_NAMES.build] = build;
  }
  return headers;
}
